import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LooseObject } from '../../../objects/loose-object';
import { MimeTypeValidator } from '../../validators/files/mime-type-validator/mime-type-validator';
import { FileService } from '../../../services/file/file.service';
import { ImporterService } from '../../../services/importer/importer.service';
import { ClientStoreService } from '../../../services/client-store.service';
import { CustomTranslateService } from '../../../services/custom-translate.service';
import { blank, data_get, data_is_true, fallback, filled, observe, OptionalValue, spf } from '../../utils/common';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, defer, EMPTY, Observable, throwError } from 'rxjs';
import { ChecklistsService } from '../../../services/checklist.service';
import { Checklist, ChecklistType } from '../../../module/checklists';
import { NotificationService } from '../../../services/notification.service';
import { DIALOG_DATA, DialogService } from '../../../services/dialog.service';

@Component({
  selector: 'convert-pdf-with-ai-dialog',
  template: `<fieldmagic-dialog
              [instance]="this"
              [label]="currentLabel"
              icon="edit">

              <ng-template fieldmagicDialogButtonsTmp>
                <button class="btn btn-primary btn-lg submit-button" type="submit" [disabled]="!uploadForm.valid || (uploading$ | async)" (click)="onUploadPdf()">
                  <button-icon-loader [defaultIcon]="['far', 'file-import']" [isLoading]="uploading$ | async"></button-icon-loader>
                    {{ fileLabel | translate }}
                </button>
              </ng-template>

              <ng-template fieldmagicDialogContentTmp>
                <form [formGroup]="uploadForm" (ngSubmit)="onUploadPdf()">
                  <div class="col-md-12 mt-3 upload-wrapper">
                    <drag-and-drop-file-upload-input [accept]="'.pdf'" [multiple]="false" formControlName="pdfFile"></drag-and-drop-file-upload-input>
                  </div>
                  <div class="col-md-12 mt-3 upload-wrapper">
                    <fieldmagic-textarea-input
                      [standalone]="false"
                      label="Tell me more about the checklist to be generated (This is optional)."
                      formControlName="specifics"
                      placeholder="Like standards, etc.."
                      *ngIf="moduleOrigin == 'checklist'"
                    >
                    </fieldmagic-textarea-input>
                  </div>
                </form>
              </ng-template>
            </fieldmagic-dialog>`
})
export class ConvertPdfWithAiComponent implements OnInit {

  get self() {
    return this;
  }
  public objFiles: LooseObject = {};

  fileLabel = this.customTranslate.getTranslation('upload');

  specifics?: string;
  currentLabel?: string;
  moduleOrigin?: string;

  uploadForm: FormGroup = this.formBuilder.group({
    pdfFile: new FormControl('', [Validators.required, this.mimeTypeValidator.pdf]),
    specifics: new FormControl()
  });

  readonly uploading$ = new BehaviorSubject<boolean>(false);

  constructor(
    protected mimeTypeValidator: MimeTypeValidator,
    protected formBuilder: FormBuilder,
    private fileService: FileService,
    protected importerService: ImporterService,
    private clientStoreService: ClientStoreService,
    private customTranslate: CustomTranslateService,
    private checklistService: ChecklistsService,
    @Inject(DIALOG_DATA) public data: any,
    private _dialog: DialogService

  ) { }

  ngOnInit() {
    if (filled(this.data)) {
      this.currentLabel = this.data['label'];
      this.moduleOrigin = this.data['moduleOrigin'];
    }
  }

  onUploadPdf = () => observe({
    before: () => this.uploading$.next(true),
    after: () => this.uploading$.next(false),
    observable: () => defer(() => {
      this.fileLabel = this.customTranslate.getTranslation('extracting');
      let uploadFile = this.uploadForm.get('pdfFile').value;

      let objCurrentClient = this.clientStoreService.getActiveClient();
      let objClientConfig = fallback(JSON.parse(data_get(objCurrentClient, 'job_wizard_ai_config')), {
        fallback: () => []
      });

      let additionals = fallback(data_get(objClientConfig, 'additional_prompt'), {
        fallback: () => ''
      });

      let temperature = fallback(data_get(objClientConfig, 'temperature'), {
        fallback: () => '0.9'
      });

      return this.fileService.upload(uploadFile).pipe(
        switchMap(
          tmpFile => {
            if (tmpFile.url === null || tmpFile.filename === null) {
              return throwError(() => new Error('The uploaded file is not a PDF'));
            } else {
              let objFile = this.fileService.objFile;
              this.objFiles = {
                'name': objFile.name,
                'size': objFile.size / 1024,
                'type': objFile.type,
                'upload_name': tmpFile.filename
              };
            }

            let details = {
              path: tmpFile.filename,
              additionals: additionals,
              temperature: parseFloat(temperature),
              moduleOrigin: this.moduleOrigin
            };

            return this.fileService.extractPdf(details)
          }
        ),
        switchMap(extractedContent => {
          if (!extractedContent.success) {
            this.fileLabel = this.customTranslate.getTranslation('upload');
            return EMPTY;
          } else {
            const data = data_get(extractedContent, 'data.body');
            if (this.moduleOrigin == 'job') {
              return this.fileService.findDetails(data);
            } else {
              this.fileLabel = this.customTranslate.getTranslation('generating');
              let message = "Treat the provided text as if it were extracted from a PDF. Use it to generate additional inspection items for generating the final checklist";
              message = spf("%s: %s", {
                args: [message, data_get(data, 'text')]
              });

              const values = this.uploadForm.value;

              if (filled(data_get(values, 'specifics'))) {
                message = spf('%s. %s', {
                  args: [message, data_get(values, 'specifics')]
                });
              }

              const body = {
                checklist_type: data_get(this.data, 'checklist_type'),
                asset_type_id: data_get(this.data, 'asset_type_id'),
                checklist_name: data_get(this.data, 'checklist_name'),
                specifics: message,
                asset_type_name: data_get(this.data, 'asset_type_name'),
                preview: data_get(this.data, 'preview')
              }

              return this.checklistService.generate(body).pipe(
                catchError(error => {
                  this.fileLabel = this.customTranslate.getTranslation('upload');
                  return throwError(error);
                }
              ));
            }
          }
        })
      )
    })
  }).subscribe((response) => {
    this.fileLabel = this.customTranslate.getTranslation('upload');

    if (this.moduleOrigin == 'job') {
      response['objFiles'] = this.objFiles
    }

    this._dialog.close({
      instance: this,
      result: response
    });
  })
}

export type ChecklistsGenerateWithArtificialIntelligenceDialogResult = Checklist;

export type ChecklistsGenerateWithArtificialIntelligenceDialogData = {
  checklist_type: ChecklistType;
  checklist_name?: string;
  asset_type_id?: string;
  asset_type_name?: string;
  preview?: boolean;
  specifics?: string;
  label?: string;
  moduleOrigin?: string;
};

export type ConvertPdfJobWizardDialogData = {
  label?: string;
  moduleOrigin?: string;
};

@Component({
  selector: 'convert-pdf-with-ai-button',
  template: `
        <fieldmagic-primary-button
          variant="success"
          (click)="onUpload()"
          [isInProgress]="uploading$ | async"
          [purpose]="purpose">
        </fieldmagic-primary-button>`
})
export class ConvertPdfWithAiButtonComponent {
  @Input() data: ChecklistsGenerateWithArtificationIntelligenceButtonData;

  @Input() moduleOrigin: string;

  @Input() purpose: string;

  @Output('uploaded') $onUpload = new EventEmitter<Checklist | LooseObject>();

  readonly uploading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private notifyService: NotificationService,
    private _dialog: DialogService
  ) { }

  onUpload = () => observe({
    before: () => this.uploading$.next(true),
    after: () => this.uploading$.next(false),
    observable: () => defer(() => {
      if (this.moduleOrigin == 'checklist') {
        if (! this._isValid()) {
          return EMPTY;
        }

        const mode = fallback(this.data.mode, {
          fallback: () => 'add',
        });

        if (mode == 'edit') {
          return this.notifyService.sendConfirmation('This will override the current checklist prompts. Are you sure you wish to continue?').pipe(
            filter((confirmation) => confirmation.answer),
            switchMap(() => this._showChecklistDialog()),
          );
        }

        return this._showChecklistDialog();
      } else {
        return this._showConvertDialog();
      }
    })
  }).pipe(
      filter(filled),
      tap(() => {
        if (this.moduleOrigin == 'checklist') {
          this.notifyService.notifySuccess('Checklist successfully generated.')
        } else {
          this.notifyService.notifySuccess('extraction_successful');
        }
      }),
    ).subscribe((response) => {
    this.$onUpload.emit(response);
  });

  private _isValid(): boolean {
    const values = {
      ... this.data,
    };

    if (blank(data_get(values, 'checklist_type'))) {
      this.notifyService.notifyError('Please make sure to select what type of checklist to generate.');
      return false;
    }

    if (blank(data_get(values, 'name'))
      && data_get(values, 'checklist_type') != 'asset'
    ) {
      this.notifyService.notifyError('Please provide a checklist name.');
      return false;
    }

    if (data_get(values, 'checklist_type') == 'asset'
      && blank(data_get(values, 'asset_type_id'))
      && blank(data_get(values, 'asset_type_name'))
    ) {
      this.notifyService.notifyError('Please specify the asset type name.');
      return false;
    }

    return true;
  }

  private _showChecklistDialog = () => new Observable<OptionalValue<ChecklistsGenerateWithArtificialIntelligenceDialogResult>>((observer) => {
    this._dialog.show<ConvertPdfWithAiComponent, ChecklistsGenerateWithArtificialIntelligenceDialogData>({
      component: ConvertPdfWithAiComponent,
      data: {
        checklist_type: data_get(this.data, 'checklist_type'),
        asset_type_id: data_get(this.data, 'asset_type_id'),
        checklist_name: data_get(this.data, 'name'),
        preview: data_get(this.data, 'mode') == 'edit'
        || data_is_true(data_get(this.data, 'force_preview')),
        specifics: data_get(this.data, 'specifics'),
        label: 'pdf_to_checklist_ai',
        moduleOrigin: 'checklist',
        asset_type_name: data_get(this.data, 'asset_type_name')
      },
      afterClosing: (checklist) => observer.next(checklist),
    });
  });

  private _showConvertDialog = () => new Observable<OptionalValue<LooseObject>>((observer) => {
    this._dialog.show<ConvertPdfWithAiComponent, ConvertPdfJobWizardDialogData>({
      component: ConvertPdfWithAiComponent,
      data: {
        label: 'pdf_to_job_ai',
        moduleOrigin: 'job'
      },
      afterClosing: (extracted) => observer.next(extracted),
    });
  });
}

export type ChecklistsGenerateWithArtificationIntelligenceButtonData = {
  checklist_type: ChecklistType;
  asset_type_id?: string;
  name?: string;
  mode?: 'edit' | 'add';
  specifics?: string;
}